import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { PKCE_KEYS } from '../../utils/pkce';

/**
 * AuthCallback
 *
 * Landing page for the Moodle OAuth2 redirect after the user completes
 * login + 2FA on Moodle's own UI.
 *
 * Moodle appends ?code=...&state=... to the redirect_uri.
 *
 * This component:
 *  1. Validates the returned `state` against sessionStorage (CSRF check).
 *  2. Retrieves the `code_verifier` from sessionStorage.
 *  3. POSTs { code, code_verifier } to the Python backend for token exchange.
 *     → Backend calls Moodle's token endpoint and userinfo endpoint.
 *     → Backend returns { user, session_token }.
 *  4. Stores the session data in localStorage (matching the existing app pattern).
 *  5. Navigates to /home.
 */

/** Status messages shown in the loading UI */
const STATUS = {
  VERIFYING  : 'Verifying your identity…',
  COMPLETING : 'Completing sign-in…',
  REDIRECTING: 'Redirecting to dashboard…',
  ERROR      : 'Authentication failed.',
};

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const [statusMsg, setStatusMsg] = useState(STATUS.VERIFYING);
  const [hasError, setHasError]   = useState(false);

  /**
   * Writes the authenticated user's data into localStorage using the same
   * keys the rest of the app already reads (sidebar, header, private-page, etc.)
   */
  const persistSession = (data) => {
    // Backend response: { data: { session_token, user }, message, success }
    const inner = data.data ?? data;
    const user = inner.user ?? data.user ?? data;

    localStorage.setItem('login',          'true');
    localStorage.setItem('fullname',       user.fullname ?? `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim());
    localStorage.setItem('userpictureurl', user.userpictureurl  ?? '');
    localStorage.setItem('email',          user.email           ?? '');
    localStorage.setItem('admin',          user.isadmin ?? user.userissiteadmin ?? false);
    localStorage.setItem('user_id',        user.id ?? user.userid ?? user.user_id ?? '');

    // Store the role_id(s) — keep both a primary role_id and the full array
    const roleIds = Array.isArray(user.roleids) ? user.roleids : (user.role_id ? [user.role_id] : []);
    localStorage.setItem('role_id', roleIds.length > 0 ? roleIds[0] : '');
    localStorage.setItem('role_ids', JSON.stringify(roleIds));

    // Store session token for authenticated API requests
    if (inner.session_token) localStorage.setItem('session_token', inner.session_token);
    if (data.access_token) localStorage.setItem('access_token', data.access_token);
    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
  };

  /**
   * Calls the Python backend to exchange the authorization code for tokens
   * and retrieve the verified user identity.
   *
   * Expected request:  POST /api/auth/callback  { code, code_verifier }
   * Expected response: { user: {...}, access_token, refresh_token }
   */
  const exchangeCodeWithBackend = useCallback(async (code, codeVerifier) => {
    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/auth/callback`,
      { code, code_verifier: codeVerifier },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return data; // return full response so we can extract tokens
  }, []);

  useEffect(() => {
    const run = async () => {
      const code          = searchParams.get('code');
      const returnedState = searchParams.get('state');
      const error         = searchParams.get('error');

      // ── Handle Moodle-side errors (user denied consent, etc.) ──────────────
      if (error) {
        toast.error(`Authentication failed: ${error}`);
        setHasError(true);
        setStatusMsg(STATUS.ERROR);
        setTimeout(() => navigate('/', { replace: true }), 2500);
        return;
      }

      // ── Validate required params ───────────────────────────────────────────
      if (!code || !returnedState) {
        toast.error('Invalid callback: missing required parameters.');
        setHasError(true);
        setStatusMsg(STATUS.ERROR);
        setTimeout(() => navigate('/', { replace: true }), 2500);
        return;
      }

      // ── CSRF: validate state ───────────────────────────────────────────────
      const storedState = localStorage.getItem(PKCE_KEYS.STATE);
      if (!storedState || storedState !== returnedState) {
        toast.error('Security check failed. Please log in again.');
        localStorage.removeItem(PKCE_KEYS.STATE);
        localStorage.removeItem(PKCE_KEYS.CODE_VERIFIER);
        setHasError(true);
        setStatusMsg(STATUS.ERROR);
        setTimeout(() => navigate('/', { replace: true }), 2500);
        return;
      }

      // ── Retrieve code_verifier ─────────────────────────────────────────────
      const codeVerifier = localStorage.getItem(PKCE_KEYS.CODE_VERIFIER);
      if (!codeVerifier) {
        toast.error('Session data missing. Please log in again.');
        setHasError(true);
        setStatusMsg(STATUS.ERROR);
        setTimeout(() => navigate('/', { replace: true }), 2500);
        return;
      }

      // ── Clean up PKCE values (single-use) ───────────────────────────────────
      localStorage.removeItem(PKCE_KEYS.STATE);
      localStorage.removeItem(PKCE_KEYS.CODE_VERIFIER);

      // ── Exchange code for user identity via Python backend ─────────────────
      setStatusMsg(STATUS.COMPLETING);
      try {
        const responseData = await exchangeCodeWithBackend(code, codeVerifier);

        persistSession(responseData);

        setStatusMsg(STATUS.REDIRECTING);
        toast.success('Logged in successfully');
        navigate('/home', { replace: true });
      } catch (err) {
        console.error('[AuthCallback] Token exchange failed:', err);
        toast.error('Sign-in could not be completed. Please try again.');
        setHasError(true);
        setStatusMsg(STATUS.ERROR);
        setTimeout(() => navigate('/', { replace: true }), 2500);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="login-wrapper">
      <Container
        fluid
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: '100vh' }}
      >
        {!hasError ? (
          <>
            <Spinner animation="border" variant="primary" role="status" aria-label="Loading" />
            <p className="mt-3 text-muted">{statusMsg}</p>
          </>
        ) : (
          <>
            <p style={{ fontSize: '2.5rem' }}>⚠️</p>
            <p className="text-danger fw-semibold">{statusMsg}</p>
            <p className="text-muted" style={{ fontSize: '0.875rem' }}>
              Redirecting to login…
            </p>
          </>
        )}
      </Container>
    </section>
  );
};

export default AuthCallback;
